// src/pages/storePages/manageStore/DeleteCategoryDialog.jsx
import { useEffect, useRef, useState } from "react";
import { storeDeleteCategory } from "../../../storeAPIs/StoreCategoriesApis";
import "../../../static/storecss/manageStore/DeleteCategoryDialog.css";

export default function DeleteCategoryDialog({
  open,
  category,
  onClose,
  onDeleted,
}) {
  const dialogRef = useRef(null);
  const inputRef = useRef(null);

  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const expected = category?.name ?? "";
  const matches =
    confirmText.trim() === expected.trim() && expected.trim().length > 0;

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;

    if (open && !dlg.open) {
      dlg.showModal();
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setConfirmText("");
      setSubmitting(false);
      setError(null);
    }
  }, [open, category?.id]);

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current && !submitting) {
      onClose?.();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !matches || !category) return;

    setSubmitting(true);
    setError(null);

    try {
      await storeDeleteCategory(category.slug);
      onDeleted?.(category);
      onClose?.();
    } catch (err) {
      const status = err.response?.status;
      const msg =
        status === 409
          ? "This category has subcategories or products. Remove them first."
          : err.response?.data?.detail ||
            err.message ||
            "Could not delete. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="msr-confirm"
      onClick={handleBackdropClick}
      aria-labelledby="msr-confirm-title"
    >
      <form className="msr-confirm-form" onSubmit={handleSubmit}>
        <div className="msr-confirm-body">
          <div className="msr-confirm-icon">
            <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          </div>

          <div className="msr-confirm-text">
            <h2 id="msr-confirm-title">Delete category</h2>
            <p>
              This will permanently delete <strong>{category?.name}</strong> and
              all of its settings. This action cannot be undone.
            </p>
          </div>
        </div>

        <div className="msr-confirm-field">
          <label htmlFor="msr-confirm-input">
            Type <strong>{category?.name}</strong> to confirm
          </label>
          <input
            ref={inputRef}
            id="msr-confirm-input"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={category?.name ?? ""}
            autoComplete="off"
            spellCheck={false}
            disabled={submitting}
          />
        </div>

        {error && <div className="msr-confirm-error">{error}</div>}

        <footer className="msr-confirm-footer">
          <button
            type="button"
            className="msr-btn msr-btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="msr-btn msr-btn--danger"
            disabled={!matches || submitting}
          >
            {submitting ? "Deleting…" : "Delete category"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}